import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'
import type { PropostaIaState } from '../components/PropostaIaCard'

/**
 * Insights da IA por painel — estado do botão «Insights» do cabeçalho.
 *
 * Cada tela que já tem uma leitura da IA (PropostaIaCard) se registra aqui com
 * `useRegisterPanelInsights`; a gaveta do cabeçalho mostra essa leitura para o
 * painel aberto no momento. Telas sem registro caem no motor por domínio
 * (`/insights/resumo`, o mesmo do AiInsightPanel).
 *
 * O «foco» é um texto livre por painel (guardado neste navegador) que entra no
 * payload da proposta como `foco_usuario` — a IA lê e prioriza o que a pessoa pediu.
 */

export type PanelInsightSugestao = {
  titulo: string
  detalhe: string
  destino?: string
  tom?: 'critico' | 'atencao' | 'neutro'
}

export type PanelInsightsRegistro = {
  /** Nome do painel, como aparece no menu. */
  titulo: string
  /** De onde vem a verdade desta leitura (fonte dos números). */
  verdade?: string
  /** Leitura da IA já carregada pela tela. */
  estado?: PropostaIaState
  /** Gera a leitura de novo (usa o foco atual). */
  recarregar?: () => void | Promise<void>
  /** Achados estruturados da tela (regras), se existirem. */
  sugestoes?: PanelInsightSugestao[]
  /** `false` quando a leitura vem pronta de um serviço e o foco não se aplica. */
  aceitaFoco?: boolean
}

type PanelInsightsCtx = {
  aberto: boolean
  abrir: () => void
  fechar: () => void
  alternar: () => void
  /** Registro do painel aberto agora (ou null). */
  registro: PanelInsightsRegistro | null
  registrar: (path: string, registro: PanelInsightsRegistro | null) => void
  focoDe: (path: string) => string
  setFoco: (path: string, foco: string) => void
}

const FOCO_KEY = 'fiesta-bi-insights-foco-v1'

function carregarFocos(): Record<string, string> {
  try {
    const raw = localStorage.getItem(FOCO_KEY)
    if (!raw) return {}
    const data = JSON.parse(raw) as unknown
    if (!data || typeof data !== 'object') return {}
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
      if (typeof v === 'string' && v.trim()) out[k] = v
    }
    return out
  } catch {
    return {}
  }
}

const Ctx = createContext<PanelInsightsCtx | null>(null)

export function PanelInsightsProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const [aberto, setAberto] = useState(false)
  const [registros, setRegistros] = useState<Record<string, PanelInsightsRegistro>>({})
  const [focos, setFocos] = useState<Record<string, string>>(carregarFocos)

  useEffect(() => {
    try {
      localStorage.setItem(FOCO_KEY, JSON.stringify(focos))
    } catch {
      /* quota / modo privado */
    }
  }, [focos])

  const registrar = useCallback((path: string, registro: PanelInsightsRegistro | null) => {
    setRegistros((prev) => {
      if (!registro) {
        if (!(path in prev)) return prev
        const next = { ...prev }
        delete next[path]
        return next
      }
      return { ...prev, [path]: registro }
    })
  }, [])

  const focoDe = useCallback((path: string) => focos[path] || '', [focos])
  const setFoco = useCallback((path: string, foco: string) => {
    setFocos((prev) => {
      const limpo = foco.trim()
      if (!limpo) {
        if (!(path in prev)) return prev
        const next = { ...prev }
        delete next[path]
        return next
      }
      if (prev[path] === limpo) return prev
      return { ...prev, [path]: limpo }
    })
  }, [])

  const abrir = useCallback(() => setAberto(true), [])
  const fechar = useCallback(() => setAberto(false), [])
  const alternar = useCallback(() => setAberto((v) => !v), [])

  const value = useMemo<PanelInsightsCtx>(
    () => ({
      aberto,
      abrir,
      fechar,
      alternar,
      registro: registros[pathname] || null,
      registrar,
      focoDe,
      setFoco,
    }),
    [aberto, abrir, fechar, alternar, registros, pathname, registrar, focoDe, setFoco],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function usePanelInsights(): PanelInsightsCtx {
  const ctx = useContext(Ctx)
  if (!ctx) {
    throw new Error('usePanelInsights precisa estar dentro de <PanelInsightsProvider>')
  }
  return ctx
}

/**
 * Foco atual do painel aberto. `focoRef` serve para ler o foco dentro de um
 * callback sem torná-lo dependência — assim mudar o foco não dispara a leitura
 * sozinho; quem dispara é o botão «Aplicar» da gaveta.
 */
export function usePanelInsightsFoco() {
  const { focoDe } = usePanelInsights()
  const { pathname } = useLocation()
  const foco = focoDe(pathname)
  const focoRef = useRef(foco)
  focoRef.current = foco
  return { foco, focoRef }
}

/**
 * Registra a leitura da tela para o botão «Insights» do cabeçalho.
 * `recarregar` fica num ref: a tela não precisa memoizar a função.
 * `sugestoes`, quando existir, deve vir memoizado (useMemo) para não re-registrar a cada render.
 */
export function useRegisterPanelInsights(registro: PanelInsightsRegistro) {
  const { registrar } = usePanelInsights()
  const { pathname } = useLocation()
  const recarregarRef = useRef(registro.recarregar)
  recarregarRef.current = registro.recarregar
  const { titulo, verdade, estado, sugestoes, aceitaFoco } = registro
  const temRecarregar = Boolean(registro.recarregar)

  useEffect(() => {
    registrar(pathname, {
      titulo,
      verdade,
      estado,
      sugestoes,
      aceitaFoco,
      recarregar: temRecarregar ? () => recarregarRef.current?.() : undefined,
    })
    return () => registrar(pathname, null)
  }, [registrar, pathname, titulo, verdade, estado, sugestoes, aceitaFoco, temRecarregar])
}
